// Modal "Preferências" (menu do usuário → "Preferências · menu, tema, som"):
// menu lateral padrão (Users.defaultMenu), aparência, som e idioma.
import React, { useEffect, useState } from "react";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import CloseIcon from "@material-ui/icons/Close";
import MenuOpenIcon from "@material-ui/icons/MenuOpen";
import MenuIcon from "@material-ui/icons/Menu";
import CheckIcon from "@material-ui/icons/Check";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import useHeaderStyles from "./headerStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { AppearancePanel } from "./AppearancePopover";
import { SoundPanel } from "../../components/NotificationsVolume";
import { LANGUAGES } from "./headerRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const line = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  return {
    paper: { width: 560, maxWidth: "calc(100vw - 32px)", margin: 16 },
    body: { padding: "6px 24px 18px", display: "flex", flexDirection: "column", gap: 18, maxHeight: "70vh", overflowY: "auto", ...theme.scrollbarStyles },
    section: { display: "flex", flexDirection: "column", gap: 12, paddingTop: 14, borderTop: `1px solid ${line}`, "&:first-child": { borderTop: "none", paddingTop: 8 } },
    grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
    opt: { border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`, borderRadius: 10, padding: "10px 12px", display: "flex", alignItems: "center", gap: 10, background: "none", cursor: "pointer", font: "inherit", color: theme.palette.text.secondary, fontSize: 12.5, fontWeight: 600, textAlign: "left", "& .MuiSvgIcon-root": { fontSize: 18 } },
    optOn: { borderColor: "rgba(160,68,255,0.5)", backgroundColor: alpha("#A044FF", 0.08), color: theme.palette.text.primary },
    optSub: { fontSize: 11, fontWeight: 500, color: theme.palette.text.secondary },
    langs: { display: "flex", gap: 6, flexWrap: "wrap" },
    lang: { display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 9, border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`, background: "none", cursor: "pointer", font: "inherit", fontSize: 12.5, fontWeight: 600, color: theme.palette.text.secondary, "& .MuiSvgIcon-root": { fontSize: 14, color: stateText(theme, AEROS.cyan, "info") } },
    langOn: { backgroundColor: alpha("#A044FF", 0.12), color: theme.palette.text.primary, borderColor: "transparent" },
  };
});

export default function PreferencesModal({ open, onClose, user, volume, setVolume, enabledLanguages = ["pt-BR", "en"], onLanguage, onMenuChange }) {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const h = useHeaderStyles();
  const [menu, setMenu] = useState(user?.defaultMenu === "closed" ? "closed" : "open");
  // O user do AuthContext vem do login e pode estar defasado; lê o valor atual.
  useEffect(() => {
    if (!user?.id) return;
    api.get(`/users/${user.id}`).then(({ data }) => setMenu(data?.defaultMenu === "closed" ? "closed" : "open")).catch(() => {});
  }, [user?.id]);
  const [saving, setSaving] = useState(false);
  const lang = i18n.language || localStorage.getItem("language") || "pt-BR";

  const saveMenu = async (value) => {
    setMenu(value);
    setSaving(true);
    try {
      await api.put(`/users/${user.id}`, { defaultMenu: value });
      onMenuChange && onMenuChange(value);
      toast.success(i18n.t("aeros.prefs.menuSaved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth={false} classes={{ paper: classes.paper }}>
      <div className={m.form}>
        <div className={m.header}>
          <div style={{ flex: 1 }}>
            <div className={m.title}>{i18n.t("aeros.prefs.title")}</div>
            <div className={m.subtitle}>{i18n.t("aeros.prefs.subtitle")}</div>
          </div>
          <IconButton className={m.closeButton} onClick={onClose} aria-label={i18n.t("aeros.header.close")}><CloseIcon /></IconButton>
        </div>
        <div className={classes.body}>
          <div className={classes.section}>
            <div className={h.sectionLabel} style={{ marginBottom: 0 }}>{i18n.t("aeros.prefs.menu")}</div>
            <div className={classes.grid2}>
              <button type="button" className={`${classes.opt} ${menu === "open" ? classes.optOn : ""}`} onClick={() => saveMenu("open")} disabled={saving} aria-pressed={menu === "open"}>
                <MenuOpenIcon /><div>{i18n.t("aeros.prefs.menuOpen")}<div className={classes.optSub}>{i18n.t("aeros.prefs.menuOpenDesc")}</div></div>
              </button>
              <button type="button" className={`${classes.opt} ${menu === "closed" ? classes.optOn : ""}`} onClick={() => saveMenu("closed")} disabled={saving} aria-pressed={menu === "closed"}>
                <MenuIcon /><div>{i18n.t("aeros.prefs.menuClosed")}<div className={classes.optSub}>{i18n.t("aeros.prefs.menuClosedDesc")}</div></div>
              </button>
            </div>
          </div>
          <div className={classes.section}>
            <div className={h.sectionLabel} style={{ marginBottom: 0 }}>{i18n.t("aeros.prefs.appearance")}</div>
            <AppearancePanel />
          </div>
          <div className={classes.section}>
            <div className={h.sectionLabel} style={{ marginBottom: 0 }}>{i18n.t("aeros.header.sound")}</div>
            <SoundPanel volume={volume} setVolume={setVolume} />
          </div>
          <div className={classes.section}>
            <div className={h.sectionLabel} style={{ marginBottom: 0 }}>{i18n.t("aeros.header.language")}</div>
            <div className={classes.langs}>
              {LANGUAGES.filter((l) => enabledLanguages.includes(l.code)).map((l) => (
                <button key={l.code} type="button" className={`${classes.lang} ${l.code === lang ? classes.langOn : ""}`} onClick={() => onLanguage(l.code)}>
                  {l.code === lang && <CheckIcon />}{l.short} · {l.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className={m.footer}>
          <div className={m.footerActions}>
            <Button className={m.submitButton} onClick={onClose}>{i18n.t("aeros.prefs.done")}</Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
